// Android "Jaa"-valikosta saapuva GPX-, TCX- tai FIT-tiedosto (natiivi plugin: ShareReceiverPlugin.java).
//
// Kulku: natiivi muistaa jaetun tiedoston ja kertoo "shareReceived"-tapahtumalla, jos sovellus on jo auki.
// Käyttöliittymä hakee tiedoston `take()`-kutsulla heti käynnistyttyään ja aina tapahtuman jälkeen. Tiedosto
// ohjataan samaan esikatseluun kuin "Tuo retki tiedostosta": mitään ei tallenneta ennen käyttäjän valintaa.
//
// Plugin annetaan tehtaalle parametrina, jotta logiikan voi testata ilman Androidia.

import { registerPlugin } from "@capacitor/core";

const isNative = () => typeof window !== "undefined" && Boolean(window.Capacitor?.isNativePlatform?.());

/** MIME-tyyppi -> pääte. Vain tuetut muodot; muu tunnistetaan palvelimella tiedoston sisällöstä. */
const EXTENSIONS = {
  "application/gpx+xml": ".gpx",
  "application/vnd.garmin.tcx+xml": ".tcx",
  "application/vnd.ant.fit": ".fit",
};

/**
 * Tiedoston nimi esikatselulle. Palvelin tunnistaa muodon päätteestä tai sisällöstä; nimi on lähinnä
 * näkyvä otsikko, mutta pääte autetaan kohdalleen, kun jakaja ei anna nimeä tai antaa vain tunnisteen
 * ("document:1234"). Nimen polkuosa ja kiellettyjä merkkejä sisältävät osat siivotaan.
 */
export function sharedFileName(name, mime) {
  const clean = String(name ?? "")
    .split(/[\\/]/)
    .pop()
    .replace(/[^\p{L}\p{N}._ ()-]+/gu, "_")
    .trim()
    .slice(0, 120);
  const ext = EXTENSIONS[String(mime ?? "").toLowerCase()];
  if (!clean || /^[_.]+$/.test(clean)) return `jaettu-retki${ext ?? ""}`;
  if (ext && !/\.(gpx|tcx|fit)$/i.test(clean)) return `${clean}${ext}`;
  return clean;
}

/** Virheen teksti käyttäjälle. Natiivi antaa koodin (too_large, read_failed); muu on odottamaton. */
export function shareErrorText(error) {
  switch (error?.code) {
    case "too_large":
      return "Jaettu tiedosto on liian suuri (yli 20 Mt).";
    case "read_failed":
      return "Jaettua tiedostoa ei voitu lukea. Kokeile jakaa se uudelleen.";
    default:
      return "Jaetun tiedoston vastaanotto epäonnistui.";
  }
}

/**
 * Tehdas: { supported, take, listen }.
 *   take()   -> { name, b64 } tai null, kun mitään ei odota. Heittää { code, message } virheestä.
 *   listen() -> kuuntelee sovelluksen ollessa auki saapuvia jakoja; palauttaa funktion, joka lopettaa kuuntelun.
 */
export function createShareImport(plugin, native = isNative) {
  return {
    supported: native(),
    async take() {
      if (!native()) return null;
      const got = await plugin.getPending();
      if (!got?.pending || !got.base64) return null;
      return { name: sharedFileName(got.name, got.mime), b64: got.base64 };
    },
    listen(callback) {
      if (!native()) return () => {};
      let handle = null;
      let stopped = false;
      Promise.resolve(plugin.addListener("shareReceived", callback)).then((h) => {
        // Jos kuuntelu ehdittiin lopettaa ennen kuin rekisteröinti valmistui, poistetaan se heti.
        if (stopped) h?.remove?.();
        else handle = h;
      });
      return () => {
        stopped = true;
        handle?.remove?.();
      };
    },
  };
}

export const shareImport = createShareImport(registerPlugin("ShareReceiver"));
